import { DS_CUSTOM_ELEMENT_NAME, customElementTag } from "@/src/design_system/shared/constants/customElements";
import {
  DS_TOOLTIP_ANCHOR_NAME,
  DS_TOOLTIP_ATTRIBUTES,
  DS_TOOLTIP_ID,
  DS_TOOLTIP_SELECTOR,
} from "../constants/attributes";
import { DS_TOOLTIP_CLASSES } from "../constants/classes";
import { DS_TOOLTIP_PLACEMENTS, type DSTooltipOptions, type DSTooltipPlacement } from "../types/options";

const DEFAULT_PLACEMENT: DSTooltipPlacement = DS_TOOLTIP_PLACEMENTS.Bottom;
type TooltipSide = "top" | "bottom" | "left" | "right";

const OPPOSITE_TOOLTIP_SIDE: Record<TooltipSide, TooltipSide> = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};

const nonBlankValue = (value: string | null): string | undefined =>
  value !== null && value.trim() ? value : undefined;

export const normalizeTooltipPlacement = (value: string | null | undefined): DSTooltipPlacement =>
  Object.values(DS_TOOLTIP_PLACEMENTS).includes(value as DSTooltipPlacement)
    ? (value as DSTooltipPlacement)
    : DEFAULT_PLACEMENT;

export const parseTooltipOptions = (anchor: Element): DSTooltipOptions | null => {
  const text = nonBlankValue(anchor.getAttribute(DS_TOOLTIP_ATTRIBUTES.Text));
  if (!text) return null;

  const kbd = nonBlankValue(anchor.getAttribute(DS_TOOLTIP_ATTRIBUTES.KeyboardShortcut));

  return {
    text,
    title: nonBlankValue(anchor.getAttribute(DS_TOOLTIP_ATTRIBUTES.Title)),
    kbd,
    kbdMac: kbd ? nonBlankValue(anchor.getAttribute(DS_TOOLTIP_ATTRIBUTES.KeyboardShortcutMac)) : undefined,
    placement: normalizeTooltipPlacement(anchor.getAttribute(DS_TOOLTIP_ATTRIBUTES.Placement)),
  };
};

const configureTooltip = (tooltip: HTMLElement): HTMLElement => {
  tooltip.id = DS_TOOLTIP_ID;
  tooltip.className = DS_TOOLTIP_CLASSES.Tooltip;
  tooltip.setAttribute("popover", "hint");
  tooltip.setAttribute("role", "tooltip");
  return tooltip;
};

export const getOrCreateTooltip = (ownerDocument: Document = document, currentTooltip?: HTMLElement): HTMLElement => {
  const existingTooltip = currentTooltip ?? ownerDocument.getElementById(DS_TOOLTIP_ID);
  const tooltip = configureTooltip(existingTooltip ?? ownerDocument.createElement("div"));

  if (!tooltip.isConnected) ownerDocument.body.append(tooltip);

  return tooltip;
};

export const renderTooltipContent = (tooltip: HTMLElement, options: DSTooltipOptions): void => {
  tooltip.replaceChildren();

  if (options.title) {
    const title = tooltip.ownerDocument.createElement("div");
    title.className = DS_TOOLTIP_CLASSES.Title;
    title.textContent = options.title;
    tooltip.append(title);
  }

  const body = tooltip.ownerDocument.createElement("div");
  body.className = DS_TOOLTIP_CLASSES.Body;

  const text = tooltip.ownerDocument.createElement("span");
  text.className = DS_TOOLTIP_CLASSES.Text;
  text.textContent = options.text;
  body.append(text);

  if (options.kbd) {
    const shortcut = tooltip.ownerDocument.createElement(customElementTag(DS_CUSTOM_ELEMENT_NAME.KeyboardShortcut));
    shortcut.setAttribute("data-text", options.kbd);
    if (options.kbdMac) shortcut.setAttribute("data-mac-text", options.kbdMac);
    body.append(shortcut);
  }

  tooltip.append(body);
};

// Inline styles are the only way to beat component resets such as
// `.ds-button { all: revert; }`, which would neutralize a class or attribute rule.
export const setTooltipAnchorName = (anchor: HTMLElement): void => {
  anchor.style.setProperty("anchor-name", DS_TOOLTIP_ANCHOR_NAME);
};

export const clearTooltipAnchorName = (anchor: HTMLElement): void => {
  anchor.style.removeProperty("anchor-name");
  if (!anchor.getAttribute("style")) anchor.removeAttribute("style");
};

export const eventAnchor = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof Element)) return null;
  const anchor = target.closest(DS_TOOLTIP_SELECTOR);
  return anchor instanceof HTMLElement && parseTooltipOptions(anchor) ? anchor : null;
};

const ariaDescriptionTokens = (anchor: HTMLElement): string[] =>
  (anchor.getAttribute("aria-describedby") ?? "").split(/\s+/).filter(Boolean);

export const addTooltipDescription = (anchor: HTMLElement): void => {
  const tokens = ariaDescriptionTokens(anchor);
  if (tokens.includes(DS_TOOLTIP_ID)) return;
  anchor.setAttribute("aria-describedby", [...tokens, DS_TOOLTIP_ID].join(" "));
};

export const removeTooltipDescription = (anchor: HTMLElement): void => {
  const tokens = ariaDescriptionTokens(anchor).filter(token => token !== DS_TOOLTIP_ID);
  if (tokens.length) anchor.setAttribute("aria-describedby", tokens.join(" "));
  else anchor.removeAttribute("aria-describedby");
};

export const setRequestedTooltipPlacement = (tooltip: HTMLElement, placement: DSTooltipPlacement): void => {
  tooltip.setAttribute(DS_TOOLTIP_ATTRIBUTES.RequestedPlacement, placement);
};

export const setResolvedTooltipPlacement = (tooltip: HTMLElement, placement: DSTooltipPlacement): void => {
  tooltip.setAttribute(DS_TOOLTIP_ATTRIBUTES.ResolvedPlacement, placement);
};

const placementSide = (placement: DSTooltipPlacement): TooltipSide => placement.split("-")[0] as TooltipSide;

const placementAlignment = (placement: DSTooltipPlacement): string | undefined => placement.split("-")[1];

export const measureResolvedTooltipPlacement = (
  anchor: HTMLElement,
  tooltip: HTMLElement,
  requestedPlacement: DSTooltipPlacement
): DSTooltipPlacement => {
  const anchorRect = anchor.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const distances = {
    top: Math.abs(anchorRect.top - tooltipRect.bottom),
    bottom: Math.abs(tooltipRect.top - anchorRect.bottom),
    left: Math.abs(anchorRect.left - tooltipRect.right),
    right: Math.abs(tooltipRect.left - anchorRect.right),
  };
  const requestedSide = placementSide(requestedPlacement);
  const oppositeSide = OPPOSITE_TOOLTIP_SIDE[requestedSide];
  const resolvedSide = distances[oppositeSide] < distances[requestedSide] ? oppositeSide : requestedSide;
  const alignment = placementAlignment(requestedPlacement);

  return (alignment ? `${resolvedSide}-${alignment}` : resolvedSide) as DSTooltipPlacement;
};
